// LUMIVIS 3D — Phase I.2B/I.2C cart-side expiry check. A private
// `_lumivis_photo_ref` line-item property can still sit in a customer's
// Shopify cart well past its retention window (see
// docs/DATA_RETENTION.md) if they simply never return to check out — the
// uploaded photo behind it may already be deleted. This module checks
// every personalized cart line's reference against
// public/upload_status_check.php whenever the cart drawer/cart page is
// relevant, and blocks the theme's own checkout button (with a clear,
// non-technical message) if any reference is no longer usable.
//
// IMPORTANT, stated plainly so this is never mistaken for more than it
// is: this is a UX safeguard for the NORMAL theme checkout flow, not a
// server-side checkout gate. It cannot stop a deliberately crafted direct
// request to Shopify's checkout that bypasses this theme's own JS. Manual
// staff review of every personalized order remains mandatory regardless
// — see docs/STAFF_ORDER_LINKING.md. This file also never receives or
// displays the technical reference value itself, only the resulting
// available/expired_or_missing/linked state.
//
// Phase I.2C: reads the exact same site-wide
// `data-lumivis-upload-api-base-url` (on <body>, from
// layout/theme.liquid) that assets/lumivis-direct-upload.js reads — see
// that file's own docblock and lumivis-api-config.js. There is no
// separate "status check" API URL anymore, and this file never passes
// allowLocalHttpTest to resolveApiBase() — no Shopify theme setting
// reaches that parameter.
//
// Phase J.5.1 — fixed two real fail-open bugs found in this file:
// (1) the checkout-status decision used to be a DENYLIST of one value
// ("block only if a reference is 'expired_or_missing'"), so a 'linked'
// reference (already tied to a PAST order — see
// public/upload_status_check.php's resolveStatusState() and
// docs/STAFF_ORDER_LINKING.md, "already linked to a different order") or
// any unrecognized/future status string fell through to the final
// "everything is fine, unlock" branch. Fixed to a strict ALLOWLIST: only
// an explicit 'available' response, for every single reference, unlocks
// checkout — anything else (expired_or_missing, linked, an unknown
// string, a network failure) blocks. (2) a missing/invalid API
// configuration used to `return` silently while the checkout button was
// left in whatever state it was already in — safe only by accident,
// never explicit. Now the config check happens AFTER confirming the cart
// actually contains a personalized reference (so a normal cart is never
// affected by a missing upload API at all), and an invalid/missing
// config on an actually-personalized cart now explicitly blocks checkout
// with a customer-facing message instead of silently doing nothing.
// Server-side, snippets/cart-summary.liquid now also renders the
// checkout button pre-disabled (with this module's own ownership marker)
// whenever the cart already contains a personalized item at PAGE LOAD —
// this file's job is now exclusively to UNLOCK a safe cart, never to be
// the only thing standing between page-load and an active button.
import { ThemeEvents } from '@theme/events';
import { API_PATHS, resolveApiBase } from './lumivis-api-config.js';

const CHECKOUT_BUTTON_SELECTOR = '[name="checkout"]';
const MESSAGE_SELECTOR = '.lumivis-cart-photo-error';

/** @type {{ ok: true, base: string } | { ok: false } | null} */
let configResult = null;

function getConfig() {
  if (configResult !== null) return configResult;
  // Same site-wide attributes lumivis-direct-upload.js reads — see that
  // file's own docblock for why data-lumivis-allow-local-http-test is
  // never rendered by any Liquid template (no schema field exists for
  // it), so it is always absent, and therefore always false, on every
  // real Shopify-rendered page.
  const rawBase = document.body?.dataset.lumivisUploadApiBaseUrl || '';
  const allowLocalHttpTest = document.body?.dataset.lumivisAllowLocalHttpTest === 'true';
  configResult = resolveApiBase(rawBase, allowLocalHttpTest);
  if (!configResult.ok && rawBase) {
    // Merchant/developer-facing detail only — never shown to the customer.
    console.error('lumivis-cart-photo-check: invalid or disallowed API base URL configured, expiry check disabled.');
  }
  return configResult;
}

// Phase J.5.1: mirrors the same handle list used server-side in
// snippets/cart-summary.liquid's `lumivis_personalized_handles` — this
// file cannot literally share a Liquid variable across runtimes, so this
// is the ONE place in this file's own JS runtime the handle is named. Used
// only to catch the anomalous case a reference-based check alone cannot:
// a personalized product line present WITHOUT its `_lumivis_photo_ref`
// property at all (structurally shouldn't happen via this theme's own UI —
// see blocks/_lumivis-personalization-studio.liquid, which is the only
// path that ever adds this product and always sets the property in the
// same atomic submit — but a forged/direct /cart/add.js call or a stale
// pre-studio cart line could still produce it, and there is then nothing
// to send to upload_status_check.php at all).
const PERSONALIZED_PRODUCT_HANDLES = ['schlusselanhanger-personalisiert'];

/**
 * Every distinct personalized reference currently in the cart, with the
 * cart item(s) that carry it (for the customer-facing message/link), plus
 * whether any personalized-product line is missing its reference entirely.
 */
async function collectPersonalizedReferences() {
  let cart;
  try {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('cart_fetch_failed');
    cart = await response.json();
  } catch {
    return { ok: false, references: [], hasItemWithoutReference: false };
  }

  /** @type {Map<string, {url: string, title: string}[]>} */
  const byReference = new Map();
  let hasItemWithoutReference = false;
  for (const item of cart.items || []) {
    const reference = item.properties?.['_lumivis_photo_ref'];
    if (typeof reference !== 'string' || reference === '') {
      if (PERSONALIZED_PRODUCT_HANDLES.includes(item.handle)) {
        hasItemWithoutReference = true;
      }
      continue;
    }
    const entry = byReference.get(reference) || [];
    entry.push({ url: item.url || '/cart', title: item.product_title || item.title || '' });
    byReference.set(reference, entry);
  }
  return { ok: true, references: byReference, hasItemWithoutReference };
}

/**
 * @param {string} apiBase
 * @param {string} reference
 * @returns {Promise<{ ok: true, state: string } | { ok: false }>}
 */
async function checkReferenceStatus(apiBase, reference) {
  try {
    const response = await fetch(`${apiBase}/${API_PATHS.statusCheck}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reference }),
    });
    if (!response.ok) return { ok: false };
    const json = await response.json();
    if (typeof json.state !== 'string') return { ok: false };
    return { ok: true, state: json.state };
  } catch {
    return { ok: false };
  }
}

/**
 * Ownership-marker approach, not a "was it already disabled" snapshot:
 * a snapshot can't distinguish "Liquid disabled this for an empty cart"
 * from "this module itself disabled it on a PREVIOUS run" once the
 * button is already sitting there disabled — the second run would see
 * "already disabled" either way and could wrongly treat a Liquid-driven
 * disable as its own. Instead, this module only ever removes `disabled`
 * from a button it itself marked via `data-lumivis-disabled-by-check`,
 * and never touches a disabled button lacking that marker.
 * @param {boolean} blocked
 */
function setCheckoutBlocked(blocked) {
  document.querySelectorAll(CHECKOUT_BUTTON_SELECTOR).forEach((button) => {
    if (blocked) {
      button.setAttribute('disabled', 'disabled');
      button.dataset.lumivisDisabledByCheck = 'true';
    } else if (button.dataset.lumivisDisabledByCheck === 'true') {
      button.removeAttribute('disabled');
      delete button.dataset.lumivisDisabledByCheck;
    }
  });
}

/**
 * @param {string} message
 */
function showMessage(message) {
  document.querySelectorAll(MESSAGE_SELECTOR).forEach((el) => {
    el.textContent = message;
    el.hidden = message === '';
  });
}

let checkInFlight = false;
// Phase J.5.1: a trigger arriving while a check is already running used to
// be dropped outright, relying on "the next real trigger will come along
// eventually" — not a real guarantee. Now it just requests one more pass
// once the current pass finishes, so the LATEST cart state is always the
// one a completed check actually reflects. Because performCheck() runs
// are fully serialized (never concurrent — the next one only starts after
// the previous `await` chain has entirely finished), there is no scenario
// where an older, slower check could overwrite the result of a newer one:
// there is only ever one check in flight, and any trigger that arrives
// during it causes exactly one more check afterward, reading fresh
// /cart.js data at that later point in time.
let rerunRequested = false;

async function runCheck() {
  if (checkInFlight) {
    rerunRequested = true;
    return;
  }

  checkInFlight = true;
  try {
    do {
      rerunRequested = false;
      await performCheck();
    } while (rerunRequested);
  } finally {
    checkInFlight = false;
  }
}

/**
 * Applies a check's outcome to the DOM — UNLESS a newer trigger has
 * already arrived (rerunRequested is true) while this particular check
 * was still waiting on the network. Without this guard, a check based on
 * an older cart snapshot could still win a brief race: it finishes,
 * applies its (by-then-stale) "unlocked" result, and only the SUBSEQUENT
 * loop iteration corrects it a moment later — a real, if narrow, window
 * where an outdated result was visibly (and actionably) live. Skipping
 * the apply here instead means a superseded check's result is discarded
 * outright; the button simply stays in whatever state it already had
 * (safe, since it never unlocks except through this same guarded path)
 * until the freshest check's own performCheck() call applies its result.
 * @param {boolean} blocked
 * @param {string} message
 */
function applyResult(blocked, message) {
  if (rerunRequested) return;
  setCheckoutBlocked(blocked);
  showMessage(message);
}

async function performCheck() {
  const collected = await collectPersonalizedReferences();
  if (!collected.ok) {
    // /cart.js itself failed — fail closed, but say so honestly rather
    // than claiming a photo is gone. Applies regardless of cart contents,
    // since without /cart.js this module cannot know whether the cart is
    // personalized at all.
    applyResult(true, 'Der Warenkorb konnte nicht überprüft werden. Bitte laden Sie die Seite neu und versuchen Sie es erneut.');
    return;
  }

  if (collected.hasItemWithoutReference) {
    // A personalized product line exists but carries no
    // _lumivis_photo_ref at all — there is nothing to send to
    // upload_status_check.php for it, so this can never resolve to
    // "available" no matter how many *other* references check out fine.
    // Blocks unconditionally, even if collected.references is otherwise
    // non-empty from a different, valid line.
    applyResult(true, 'Für ein personalisiertes Produkt in Ihrem Warenkorb fehlt die Foto-Referenz. Bitte entfernen Sie den Artikel aus dem Warenkorb und fügen Sie ihn mit einem Foto erneut hinzu.');
    return;
  }

  if (collected.references.size === 0) {
    // No personalized line item at all — the common case for an ordinary
    // cart. No upload_status_check.php request is made, and the upload
    // API's configuration is never even consulted: a missing/invalid
    // config must never affect a normal cart's checkout button.
    applyResult(false, '');
    return;
  }

  // From here on the cart is confirmed to contain at least one
  // personalized reference, so a missing/invalid API configuration is
  // this module's own problem to report — never a silent no-op that
  // leaves the button in whatever state it happened to be in.
  const config = getConfig();
  if (!config.ok) {
    applyResult(true, 'Ihre Personalisierung konnte derzeit nicht überprüft werden. Bitte versuchen Sie es in einem Moment erneut oder kontaktieren Sie uns.');
    return;
  }

  const entries = Array.from(collected.references.entries());
  const results = await Promise.all(entries.map(([reference]) => checkReferenceStatus(config.base, reference)));

  // Strict allowlist, not a denylist of one value: checkout unlocks ONLY
  // if every single personalized reference explicitly reports 'available'.
  // Everything else — a network/parsing failure, the documented
  // 'expired_or_missing' state, the documented 'linked' state (the
  // reference is already tied to a PAST order and must never be reused
  // for another one — see docs/STAFF_ORDER_LINKING.md), or any other,
  // unrecognized string a future backend version might send — blocks.
  const allAvailable = results.length > 0 && results.every((r) => r.ok && r.state === 'available');

  if (allAvailable) {
    applyResult(false, '');
    return;
  }

  const expiredEntries = entries.filter((_, i) => results[i].ok && results[i].state === 'expired_or_missing');
  const linkedEntries = entries.filter((_, i) => results[i].ok && results[i].state === 'linked');

  if (expiredEntries.length > 0) {
    const [, items] = expiredEntries[0];
    const productLink = items[0]?.url;
    const productTitle = items[0]?.title;
    const linkText = productTitle ? `zurück zu „${productTitle}“` : 'zurück zum Produkt';
    applyResult(
      true,
      `Ihr Personalisierungsfoto ist abgelaufen und muss erneut hochgeladen werden, bevor Sie bestellen können. `
      + `Bitte entfernen Sie den Artikel aus dem Warenkorb und fügen Sie ihn mit einem neuen Foto erneut hinzu`
      + (productLink ? ` (${linkText}).` : '.')
    );
    return;
  }

  if (linkedEntries.length > 0) {
    const [, items] = linkedEntries[0];
    const productTitle = items[0]?.title;
    const linkText = productTitle ? `„${productTitle}“` : 'diesen Artikel';
    applyResult(
      true,
      `Diese Personalisierung wurde bereits einer Bestellung zugeordnet und kann nicht erneut verwendet werden. `
      + `Bitte entfernen Sie ${linkText} aus dem Warenkorb und fügen Sie ihn mit einem neuen Foto erneut hinzu.`
    );
    return;
  }

  // Covers both an outright network/parsing failure and a well-formed but
  // unrecognized status string — in both cases nothing concrete can be
  // told to the customer beyond "not confirmed right now".
  applyResult(true, 'Der Status Ihres Personalisierungsfotos konnte nicht überprüft werden. Bitte versuchen Sie es in einem Moment erneut.');
}

document.addEventListener(ThemeEvents.cartUpdate, () => {
  runCheck();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => runCheck());
} else {
  runCheck();
}

// Best-effort: also re-check specifically when the cart drawer opens
// (dialog:open does not bubble — see assets/dialog.js — so this must
// listen on the drawer element itself once it exists), covering the case
// where a customer simply opens the drawer without any cart change
// happening in this page session at all.
function attachDrawerOpenListener() {
  const drawer = document.querySelector('cart-drawer-component');
  if (drawer) {
    drawer.addEventListener('dialog:open', () => runCheck());
  }
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', attachDrawerOpenListener);
} else {
  attachDrawerOpenListener();
}
